import React, { useEffect, useMemo, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, TextField } from "@mui/material";
import { Formik, Form, Field } from "formik";
import {
  GetMakeModel,
  PostMakeModelCombo,
  UpdateMakeModelCombo,
} from "../../../Service/_index";
import { PROVIDER_COMPANIES } from "../../../Shared/CommonConstant";
import { ToastError } from "../../../UiComponents/Toaster/Toast";
import Styles from "../../../UiComponents/Scss/AddPopup.module.scss";

const sameCompany = (a, b) =>
  !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

/**
 * The companies a combined record may pair (the owner, 2026-10-23): the OD side lists RELIANCE ONLY (Reliance is the only own-damage
 * automation), the TP side lists every company EXCEPT Reliance. The two lists never overlap, so "OD and TP must differ" can no
 * longer be hit by picking. Only this dialog offers the choice; the server still accepts what it accepted, so records saved
 * earlier with another pairing keep working (see SidePicker for how an old one is shown).
 */
export const COMBO_OD_COMPANIES = PROVIDER_COMPANIES.filter((c) => sameCompany(c, "Reliance"));
export const COMBO_TP_COMPANIES = PROVIDER_COMPANIES.filter((c) => !sameCompany(c, "Reliance"));

/** "HONDA ACTIVA 6G STD" — what a Make & Model row is called wherever it is picked. */
export const rowName = (row) =>
  [row?.make, row?.model, row?.vehicleVariant].filter(Boolean).join(" ");

const optionLabel = (row) => (row ? `${rowName(row)} · ${row.cc || "-"} cc` : "");

/** The picked row's details, so the two sides can be compared before saving. */
const RowSummary = ({ title, company, row }) => (
  <Grid item xs={12} sm={6}>
    <div
      style={{
        border: "1px solid #EAECF0",
        borderRadius: 8,
        padding: "10px 12px",
        background: "#F9FAFB",
        minHeight: 92,
      }}
    >
      <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#1E3A5F", mb: 0.5 }}>
        {title}
        {company ? ` · ${company}` : ""}
      </Typography>
      {row ? (
        <>
          <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>{rowName(row)}</Typography>
          <Typography sx={{ fontSize: "12px", color: "#475467" }}>
            {row.cc} cc · {row.fuelType || "-"} · IDV {row.idvRangeFrom} to {row.idvRangeTo}
          </Typography>
        </>
      ) : (
        <Typography sx={{ fontSize: "12px", color: "#98A2B3" }}>Not chosen yet</Typography>
      )}
    </div>
  </Grid>
);

/**
 * One side of the pairing: the company, and BELOW it that company's existing
 * Make & Models to pick one from. Changing the company clears the pick, since
 * the row belongs to the old company. `offered` = the companies this side lists;
 * a record saved earlier with a company that is no longer offered keeps showing it
 * (marked), instead of a blank box over a value that is still saved.
 */
const SidePicker = ({ label, companyField, rowField, values, setFieldValue, rows, offered }) => {
  const company = values[companyField];
  const options = useMemo(
    () => rows.filter((row) => sameCompany(row.company, company)),
    [rows, company]
  );
  const picked = rows.find((row) => String(row._id) === String(values[rowField])) || null;
  const savedOnly = company && !offered.some((c) => sameCompany(c, company)) ? company : "";

  return (
    <>
      <Grid item xs={12} sm={4}>
        <Typography className={Styles.inputTitle} sx={{ fontSize: "12px", mb: 1 }}>
          {label} Company
        </Typography>
        <Field
          name={companyField}
          as="select"
          className="InputFiled"
          onChange={(e) => {
            setFieldValue(companyField, e.target.value);
            setFieldValue(rowField, "");
          }}
        >
          <option value="">Select Company</option>
          {offered.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
          {savedOnly ? (
            <option key={savedOnly} value={savedOnly}>
              {savedOnly} (saved earlier)
            </option>
          ) : null}
        </Field>
      </Grid>
      <Grid item xs={12} sm={8}>
        <Typography className={Styles.inputTitle} sx={{ fontSize: "12px", mb: 1 }}>
          {label} Make and Model
        </Typography>
        <Autocomplete
          className="AutoComplete_InputBox"
          disablePortal
          disabled={!company}
          options={options}
          value={picked && sameCompany(picked.company, company) ? picked : null}
          onChange={(e, row) => setFieldValue(rowField, row?._id || "")}
          getOptionLabel={optionLabel}
          isOptionEqualToValue={(option, value) => String(option?._id) === String(value?._id)}
          noOptionsText={company ? `No ${company} Make & Model created yet` : "Choose the company first"}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={company ? `Select ${company} Make and Model` : "Choose the company first"}
            />
          )}
        />
      </Grid>
    </>
  );
};

const AddMakeModelCombo = (props) => {
  const { selectedData, formType, setOpenDrawer, title, GetData } = props;
  const formRef = useRef(null);
  const [rows, setRows] = useState([]);
  const UserId = sessionStorage.getItem("UserId");

  // The enabled Make & Models — the same list the Make and Model page shows. The
  // master is shared by every client (that page and the Online Policy both list
  // every client's rows), so a combo may pair any of them; the combo itself is
  // still this client's own record. On an edit the two rows the record already
  // points at are added back even if they have been disabled since, so the form
  // shows what is saved instead of an empty pick — the server still refuses to
  // save a pairing that cannot be sold.
  useEffect(() => {
    GetMakeModel().then((res) => {
      const loaded = (Array.isArray(res?.data) ? res.data : []).filter((row) => row.isEnabled === true);
      const extra = [selectedData?.odRow, selectedData?.tpRow].filter(
        (row) => row && !loaded.some((r) => String(r._id) === String(row._id))
      );
      setRows([...loaded, ...extra]);
    });
  }, [selectedData]);

  const initialValues = {
    displayName: formType === "edit" ? selectedData.displayName || "" : "",
    odCompany: formType === "edit" ? selectedData.odCompany || "" : "",
    odMakeModel: formType === "edit" ? String(selectedData.odMakeModel || "") : "",
    tpCompany: formType === "edit" ? selectedData.tpCompany || "" : "",
    tpMakeModel: formType === "edit" ? String(selectedData.tpMakeModel || "") : "",
  };

  const validate = (data) => {
    if (!data.displayName?.trim()) return "Enter the display name";
    if (!data.odCompany) return "Choose the OD company";
    if (!data.odMakeModel) return "Choose the OD Make and Model";
    if (!data.tpCompany) return "Choose the TP company";
    if (!data.tpMakeModel) return "Choose the TP Make and Model";
    if (sameCompany(data.odCompany, data.tpCompany)) {
      return "OD and TP company must be different. Use a normal Make and Model for one company.";
    }
    return "";
  };

  const onSubmit = (e, data, actionKey) => {
    const problem = validate(data);
    if (problem) {
      ToastError(problem);
      return;
    }
    const payload = { ...data, displayName: data.displayName.trim(), updatedAt: new Date() };
    if (formType === "add") payload.createdBy = UserId;
    else payload.updatedBy = UserId;

    const submit = formType === "edit" ? UpdateMakeModelCombo(selectedData._id, payload) : PostMakeModelCombo(payload);
    submit
      .then((res) => {
        if (!res) return; // refused: the service already showed why, keep the dialog open
        GetData();
        if (actionKey === "Save&Create") {
          formRef.current?.resetForm();
        } else {
          setOpenDrawer(false);
        }
      })
      .catch((error) => console.log(error));
  };

  return (
    <div className="DrawerContainer">
      <Grid container className="DrawerHeader">
        <Grid item xs={6} sm={6}>
          <Typography>{title} Combined Make and Model</Typography>
        </Grid>
        <Grid item xs={6} sm={6} className="d-flex justify-content-end">
          <CloseIcon onClick={() => setOpenDrawer(false)} sx={{ cursor: "pointer" }} />
        </Grid>
      </Grid>
      <Formik initialValues={initialValues} innerRef={formRef} onSubmit={() => {}}>
        {({ values, setFieldValue }) => {
          const odRow = rows.find((row) => String(row._id) === String(values.odMakeModel)) || null;
          const tpRow = rows.find((row) => String(row._id) === String(values.tpMakeModel)) || null;
          return (
            <Form
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
            >
              <div className={Styles.addContent}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography className={Styles.inputTitle} sx={{ fontSize: "12px", mb: 1 }}>
                      Display Name
                    </Typography>
                    <Field
                      name="displayName"
                      className="InputFiled"
                      placeholder="e.g. Activa 6G (Reliance OD + National TP)"
                    />
                    <Typography sx={{ fontSize: "11px", color: "#667085", mt: 0.5 }}>
                      This is the name shown on the Online Policy form when "Multiple company" is chosen.
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1E3A5F", pb: 0.5, borderBottom: "1px solid #eaecf0" }}>
                      OD (Own Damage)
                    </Typography>
                  </Grid>
                  <SidePicker
                    label="OD"
                    companyField="odCompany"
                    rowField="odMakeModel"
                    values={values}
                    setFieldValue={setFieldValue}
                    rows={rows}
                    offered={COMBO_OD_COMPANIES}
                  />

                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1E3A5F", pb: 0.5, borderBottom: "1px solid #eaecf0" }}>
                      TP (Third Party)
                    </Typography>
                  </Grid>
                  <SidePicker
                    label="TP"
                    companyField="tpCompany"
                    rowField="tpMakeModel"
                    values={values}
                    setFieldValue={setFieldValue}
                    rows={rows}
                    offered={COMBO_TP_COMPANIES}
                  />

                  <RowSummary title="OD vehicle" company={values.odCompany} row={odRow} />
                  <RowSummary title="TP vehicle" company={values.tpCompany} row={tpRow} />
                </Grid>
              </div>
              <div className="Dialog_Footers">
                <div className="Dialog_Button_Groups">
                  <Button onClick={() => setOpenDrawer(false)} className="Dialog_Cancels">
                    Cancel
                  </Button>
                  <Button onClick={(e) => onSubmit(e, values, "Save")} className="Dialog_Saves">
                    {selectedData?._id ? "Update" : "Save & Exit"}
                  </Button>
                </div>
                <Button
                  sx={{ display: formType === "add" ? "block" : "none", marginLeft: "10px" }}
                  className="Dialog_Save_Exits"
                  onClick={(e) => onSubmit(e, values, "Save&Create")}
                >
                  Save & Create New
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default AddMakeModelCombo;
